// components/crm/ContactDetail.tsx
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Spin,
  Tabs,
  Form,
  Input,
  Select,
  Divider,
  Space,
  Typography,
  Table,
  Modal,
  DatePicker,
  Popconfirm,
  message,
  Row,
  Col,
} from 'antd';
import {
  EditOutlined,
  SaveOutlined,
  DeleteOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useStore } from '../../stores/root-store-context';
import { Contact, CrmEvent } from '../../api/CRMApiClient';
import ERPLayout from '../../components/layout/ERPLayout';

// Add dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const toLocal = (iso: string) => dayjs(iso).local();
const toApi = (d: Dayjs) => d.utc().format("YYYY-MM-DDTHH:mm:ss");

const { TabPane } = Tabs;
const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const ContactDetail: React.FC = observer(() => {
  const { t } = useTranslation();
  const { contactId } = useParams<{ contactId?: string }>();
  const navigate = useNavigate();
  const { crmStore, applicationStore } = useStore();

  /* ─────────────────────── mode helpers ─────────────────────── */

  const isCreateMode = !contactId || contactId === 'new';
  const contactIdNumber = useMemo(
    () => (!isCreateMode && !isNaN(Number(contactId)) ? Number(contactId) : null),
    [isCreateMode, contactId],
  );

  /* ─────────────────────── state & forms ────────────────────── */

  const [form] = Form.useForm<Contact>();
  const [editing, setEditing] = useState<boolean>(isCreateMode);
  const [loading, setLoading] = useState<boolean>(true);

  const [eventModalVisible, setEventModalVisible] = useState<boolean>(false);
  const [eventForm] = Form.useForm();
  const [editingEvent, setEditingEvent] = useState<CrmEvent | null>(null);
  const [relatedEvents, setRelatedEvents] = useState<CrmEvent[]>([]);

  /* ─────────────────────── helper: build smart event name ─────────────────────── */
  const buildCompositeName = useCallback(
    (companyIds?: number[], contactIds?: number[], range?: [Dayjs, Dayjs]) => {
      const parts: string[] = [];

      if (companyIds?.length) {
        const first = crmStore.externalCompanies.find(c => c.id === companyIds[0]);
        if (first) parts.push(first.name);
        if (companyIds.length > 1) parts.push(`+${companyIds.length - 1}`);
      }

      if (contactIds?.length) {
        const first = crmStore.contacts.find(c => c.id === contactIds[0]);
        if (first) parts.push(`${first.name} ${first.surname}`.trim());
        if (contactIds.length > 1) parts.push(`+${contactIds.length - 1}`);
      }

      if (range?.[0]) parts.push(range[0].format("DD/MM/YYYY"));
      return parts.filter(Boolean).join(" — ");
    },
    [crmStore.externalCompanies, crmStore.contacts],
  );

  /* ─────────────────────── data loading ─────────────────────── */

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          crmStore.getClientRoles(),
          crmStore.getExternalCompanies(),
          crmStore.getCrmEventTypes(),
          crmStore.getContactsWithDetails(),
        ]);

        if (!isCreateMode && contactIdNumber !== null) {
          const contact = await crmStore.getContactById(contactIdNumber);
          if (contact) {
            form.setFieldsValue({
              ...contact,
              additionalInfos: contact.additionalInfos || [],
            });

            const events = await crmStore.ApiStore.CRMApiClient.getCrmEventsByContact(
              contact.id,
            );
            setRelatedEvents(events);
          }
        } else {
          form.setFieldsValue({
            name: '',
            surname: '',
            email: '',
            phoneNumber: '',
            additionalInfos: [],
          });
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateMode, contactIdNumber]);

  /* ─────────────────────── contact save ─────────────────────── */

  const onSave = async () => {
    try {
      const values = await form.validateFields();

      if (isCreateMode) {
        const created = await crmStore.addContact(values);
        message.success(t('CreatedSuccessfully'));
        navigate(`/${applicationStore.company?.Tenant}/crm/contact/${created?.id}`);
      } else if (contactIdNumber !== null) {
        await crmStore.updateContact(contactIdNumber, values);
        message.success(t('SavedSuccessfully'));
        setEditing(false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onDelete = async () => {
    if (contactIdNumber === null) return;
    await crmStore.deleteContact(contactIdNumber);
    navigate(`/${applicationStore.company?.Tenant}/crm/contacts`);
  };

  /* ─────────────────────── events helpers ───────────────────── */

  const refreshEvents = async () => {
    if (contactIdNumber === null) return;
    const events = await crmStore.ApiStore.CRMApiClient.getCrmEventsByContact(
      contactIdNumber,
    );
    setRelatedEvents(events);
  };

  const openAddEvent = () => {
    if (contactIdNumber === null) return;
    setEditingEvent(null);
    const now = dayjs();
    eventForm.setFieldsValue({
      dateRange: [now, now.clone().add(30, 'minutes')],
      contactIds: [contactIdNumber], // Use multiple contact support
      externalCompanyIds: [],
      name: '',
      description: '',
      typeId: null,
    });
    setEventModalVisible(true);
  };

  const openEditEvent = (event: CrmEvent) => {
    setEditingEvent(event);
    const dateRange: [Dayjs, Dayjs] = [
      toLocal(event.dateStart), 
      toLocal(event.dateEnd)
    ];
    
    // Map the event data properly for the full form
    const formData = {
      name: event.name,
      description: event.description,
      typeId: event.typeId,
      dateRange,
      externalCompanyIds: event.externalCompanies?.map(c => c.id) ?? [],
      contactIds: event.contacts?.map(c => c.id) ?? [],
    };
    
    eventForm.setFieldsValue(formData);
    setEventModalVisible(true);
  };

  // Auto-update generated name while typing in event form
  const handleEventValuesChange = (_: any, all: any) => {
    const composite = buildCompositeName(all.externalCompanyIds, all.contactIds, all.dateRange);
    if (composite && composite !== all.name) {
      eventForm.setFieldsValue({ name: composite });
    }
  };

  const saveEvent = async () => {
    const { dateRange, externalCompanyIds, contactIds, ...rest } = await eventForm.validateFields();
    
    const payload = {
      ...rest,
      dateStart: toApi(dateRange[0]),
      dateEnd: toApi(dateRange[1]),
      externalCompanyIds: externalCompanyIds || [],
      contactIds: contactIds || [],
      name: buildCompositeName(externalCompanyIds, contactIds, dateRange),
    };

    if (editingEvent) {
      await crmStore.updateCrmEvent(editingEvent.id, payload);
    } else {
      await crmStore.addCrmEvent(payload);
    }
    setEventModalVisible(false);
    refreshEvents();
    message.success(editingEvent ? t('SavedSuccessfully') : t('CreatedSuccessfully'));
  };

  const deleteEvent = async (id: number) => {
    await crmStore.deleteCrmEvent(id);
    refreshEvents();
    message.success(t('DeletedSuccessfully'));
  };

  /* ─────────────────────── column defs ─────────────────────── */

  const eventColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: '5%' },
    { title: t('Name'), dataIndex: 'name', key: 'name', width: '25%' },
    {
      title: t('Start'),
      dataIndex: 'dateStart',
      key: 'dateStart',
      width: '15%',
      render: (d: string) => dayjs(d).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: t('End'),
      dataIndex: 'dateEnd',
      key: 'dateEnd',
      width: '15%',
      render: (d: string) => dayjs(d).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: t('EventType'),
      dataIndex: 'typeId',
      key: 'typeId',
      width: '15%',
      render: (typeId: number) =>
        crmStore.CrmEventTypes.find((t) => t.id === typeId)?.name || '',
    },
    {
      title: t('Companies'),
      key: 'companies',
      width: '15%',
      render: (_: unknown, record: CrmEvent) => {
        const companies = record.externalCompanies || [];
        if (companies.length === 0) return '';
        if (companies.length === 1) return companies[0].name;
        return `${companies[0].name} +${companies.length - 1}`;
      },
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: '10%',
      render: (_: unknown, record: CrmEvent) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEditEvent(record)} />
          <Popconfirm
            title={t('DeleteEventConfirm')}
            onConfirm={() => deleteEvent(record.id)}
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  /* ─────────────────────── breadcrumbs ─────────────────────── */

  const breadcrumbItems = [
    { label: t('Home'), path: `/${applicationStore.company?.Tenant}` },
    { label: 'CRM', path: `/${applicationStore.company?.Tenant}/crm` },
    { label: t('Contacts'), path: `/${applicationStore.company?.Tenant}/crm/contacts` },
    {
      label: isCreateMode ? t('NewContact') : t('ContactDetails'),
      path: isCreateMode
        ? `/${applicationStore.company?.Tenant}/crm/contact/new`
        : `/${applicationStore.company?.Tenant}/crm/contact/${contactIdNumber}`,
    },
  ];

  /* ─────────────────────────── render ────────────────────────── */

  return (
    <ERPLayout breadcrumbTitle={t('ContactDetails')} breadcrumbItems={breadcrumbItems}>
      <Spin spinning={loading || crmStore.isLoading}>
        <Card
          title={
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(`/${applicationStore.company?.Tenant}/crm/contacts`)}
              />
              <Title level={4} style={{ margin: 0 }}>
                {isCreateMode
                  ? t('NewContact')
                  : crmStore.currentContact
                  ? `${crmStore.currentContact.name} ${crmStore.currentContact.surname}`
                  : t('ContactDetails')}
              </Title>
            </Space>
          }
          extra={
            <Space>
              {editing ? (
                <>
                  <Button type="primary" icon={<SaveOutlined />} onClick={onSave}>
                    {t('Save')}
                  </Button>
                  {!isCreateMode && (
                    <Button onClick={() => setEditing(false)}>{t('Cancel')}</Button>
                  )}
                </>
              ) : (
                <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
                  {t('Edit')}
                </Button>
              )}
              {!isCreateMode && (
                <Popconfirm title={t('DeleteContactConfirm')} onConfirm={onDelete}>
                  <Button danger icon={<DeleteOutlined />}>
                    {t('Delete')}
                  </Button>
                </Popconfirm>
              )}
            </Space>
          }
        >
          <Tabs defaultActiveKey="details">
            <TabPane tab={t('Details')} key="details">
              <Form form={form} layout="vertical" disabled={!editing}>
                <Row gutter={24}>
                  {/* ─────────── First column: base properties ─────────── */}
                  <Col xs={24} md={12}>
                    <Form.Item name="externalCompanyId" label={t('Company')}>
                      <Select allowClear>
                        {crmStore.externalCompanies.map((c) => (
                          <Option key={c.id} value={c.id}>
                            {c.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item name="roleId" label={t('Role')}>
                      <Select allowClear>
                        {crmStore.clientRoles.map((r) => (
                          <Option key={r.id} value={r.id}>
                            {r.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name="name"
                      label={t('Name')}
                      rules={[{ required: true, message: t('PleaseEnterName') }]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      name="surname"
                      label={t('Surname')}
                      rules={[{ required: true, message: t('PleaseEnterSurname') }]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item name="email" label={t('Email')}>
                      <Input type="email" />
                    </Form.Item>

                    <Form.Item name="phoneNumber" label={t('Phone')}>
                      <Input />
                    </Form.Item>
                  </Col>

                  {/* ─────────── Second column: Additional Contact Info ─────────── */}
                  <Col xs={24} md={12}>
                    <Divider orientation="left">{t('AdditionalContactInfo')}</Divider>
                    <Form.List name="additionalInfos">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...restField }) => (
                            <Space key={key} align="baseline" style={{ marginBottom: 8 }}>
                              <Form.Item
                                {...restField}
                                name={[name, 'type']}
                                rules={[{ required: true, message: t('PleaseSelectType') }]}
                              >
                                <Select placeholder={t('Type')} style={{ width: 150 }}>
                                  <Option value="Email">Email</Option>
                                  <Option value="LinkedIn">LinkedIn</Option>
                                  <Option value="Skype">Skype</Option>
                                  <Option value="Telegram">Telegram</Option>
                                  <Option value="Phone">Phone</Option>
                                </Select>
                              </Form.Item>
                              <Form.Item
                                {...restField}
                                name={[name, 'name']}
                                rules={[{ required: true, message: t('PleaseEnterValue') }]}
                              >
                                <Input placeholder={t('Value')} style={{ width: 200 }} />
                              </Form.Item>
                              {editing && (
                                <MinusCircleOutlined onClick={() => remove(name)} />
                              )}
                            </Space>
                          ))}
                          {editing && (
                            <Form.Item>
                              <Button
                                type="dashed"
                                onClick={() => add()}
                                block
                                icon={<PlusCircleOutlined />}
                              >
                                {t('AddField')}
                              </Button>
                            </Form.Item>
                          )}
                        </>
                      )}
                    </Form.List>
                  </Col>
                </Row>
              </Form>
            </TabPane>

            {/* Events tab only for existing contacts */}
            {!isCreateMode && (
              <TabPane tab={t('Events')} key="events">
                <div style={{ marginBottom: 16 }}>
                  <Button type="primary" icon={<PlusOutlined />} onClick={openAddEvent}>
                    {t('AddEvent')}
                  </Button>
                </div>

                <Table
                  dataSource={relatedEvents}
                  columns={eventColumns}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  expandable={{
                    expandedRowRender: (record) => (
                      <div style={{ padding: 16 }}>
                        <Text strong>{t('Description')}:</Text>
                        <Typography.Paragraph>
                          {record.description || t('NoDescription')}
                        </Typography.Paragraph>
                      </div>
                    ),
                  }}
                />
              </TabPane>
            )}
          </Tabs>
        </Card>

        {/* ─────────── Modal: Add / Edit event - FULL FORM MATCHING EventDetail ─────────── */}
        <Modal
          title={editingEvent ? t('EditEvent') : t('AddEvent')}
          open={eventModalVisible}
          onOk={saveEvent}
          onCancel={() => setEventModalVisible(false)}
          width={800}
          destroyOnClose
          okText={t('OK')}
          cancelText={t('Cancel')}
        >
          <Form 
            form={eventForm} 
            layout="vertical"
            onValuesChange={handleEventValuesChange}
          >
            <Form.Item name="externalCompanyIds" label={t('Companies')}>
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="children"
                placeholder={t('Select')}
              >
                {crmStore.externalCompanies.map((c) => (
                  <Option key={c.id} value={c.id}>
                    {c.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="contactIds" label={t('Contacts')}>
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="children"
                placeholder={t('Select')}
              >
                {crmStore.contacts.map((c) => (
                  <Option key={c.id} value={c.id}>
                    {c.name} {c.surname}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="dateRange"
              label={t('DateTime')}
              rules={[{ required: true, message: t('PleaseSelectDateTime') }]}
            >
              <RangePicker 
                showTime 
                allowClear={false} 
                format="DD/MM/YYYY HH:mm" 
                style={{ width: '100%' }} 
              />
            </Form.Item>

            <Form.Item
              name="typeId"
              label={t('EventType')}
              rules={[{ required: true, message: t('PleaseSelectEventType') }]}
            >
              <Select placeholder={t('Select')} allowClear>
                {crmStore.CrmEventTypes.map((type) => (
                  <Option key={type.id} value={type.id}>
                    {type.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item 
              name="name" 
              label={t('Name')} 
              rules={[{ required: true }]}
            >
              <Input disabled />
            </Form.Item>

            <Form.Item name="description" label={t('Description')}>
              <TextArea rows={4} />
            </Form.Item>
          </Form>
        </Modal>
      </Spin>
    </ERPLayout>
  );
});

export default ContactDetail;